import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, InputNumber, Modal, Switch, Tag } from 'antd';
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import CommercialSelect from '../com/CommercialSelect';
import { FamilleSelect, SousFamilleSelect, FournisseurSelect } from '../article/ArticleRelationSelects';

const EditContext = createContext(null);
export const RecordEditProvider = EditContext.Provider;

// Explicit editable fields also form the update allowlist. Identifiers and relations are never submitted.
const textFields = names => Object.fromEntries(names.split(' ').map(name => [name, {}]));
const numbers = (names, integer = false) => Object.fromEntries(names.split(' ').map(name => [name, { number: true, integer }]));
export const articleFields = {
  ...textFields('ARTICLE code_fam code_sfam CODE_MAR REF_OEM code_frs substitution Remarque'),
  code_fam: { component: FamilleSelect },
  code_sfam: { component: SousFamilleSelect, componentProps: editor => ({ familyCode: editor.value('code_fam') }) },
  code_frs: { component: FournisseurSelect },
  ...numbers('PA_HT PA_TTC PV_HT PV_TTC TVA Remise'),
  ...numbers('STK_MIN STK_MAX STK_SEC qt_cart q_paquet uav', true),
  ARTICLE: { required: true },
  ...Object.fromEntries(['PA_HT', 'PA_TTC', 'PV_HT', 'PV_TTC', 'TVA', 'STK_MIN', 'STK_MAX'].map(name => [name, { number: true, integer: name.startsWith('STK'), required: true }])),
  TVA: { number: true, required: true, min: 0 },
  VENTE_BLOQ: { boolean: true }, ACHAT_BLOQ: { boolean: true },
};
export const clientFields = {
  ...textFields('CLIENT CONTACT email TEL MOBILE VILLE PAYS nat_clt ADRESSE_SIEGE ADRESSE_LIV CODE_PST MODELIV port_p reg_trans NOTES MODE_PAIE mode_paie_final conditions BANQUE COMPTEBNQ ICE PATENTE FORMJUR CAPSOC'),
  ...numbers('CODE_COM CODE_CAT ECHEANCE', true),
  CODE_COM: { number: true, integer: true, component: CommercialSelect },
  ...numbers('objectif REMISE_G PLAFOND_SLD plaf_ech remise_sp SOLDE'),
  CLIENT: { required: true }, VILLE: { required: true }, ECHEANCE: { number: true, integer: true, required: true },
  BLOQUER: { boolean: true }, groupe: { boolean: true },
};

export function useRecordEditor({ record, recordId, fields, allowed, excluded = [], update, onSaved, onInvalid, deriveChanges }) {
  const [editing, setEditing] = useState(false);
  const [changes, setChanges] = useState({});
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  useEffect(() => { setEditing(false); setChanges({}); setErrors({}); setError(''); }, [recordId]);
  useEffect(() => { if (!allowed) setEditing(false); }, [allowed]);
  const dirty = Object.keys(changes).length > 0;
  useEffect(() => {
    if (!editing || !dirty) return;
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [editing, dirty]);
  const cancel = () => { if (pending.current) return; setEditing(false); setChanges({}); setErrors({}); setError(''); };
  const leave = callback => {
    if (pending.current) return;
    if (!editing || !dirty) return callback();
    Modal.confirm({ title: 'Abandonner les modifications ?', content: 'Les modifications non enregistrées seront perdues.', okText: 'Abandonner', cancelText: 'Continuer à modifier', onOk: () => { cancel(); callback(); } });
  };
  const save = async () => {
    if (!allowed || !editing || pending.current) return;
    const payload = Object.fromEntries(Object.entries(changes).filter(([key]) => fields[key] && !excluded.includes(key)));
    const invalid = {};
    for (const [key, value] of Object.entries(payload)) {
      const field = fields[key];
      if (field.required && (value === null || value === '' || (typeof value === 'string' && !value.trim()))) invalid[key] = 'Ce champ est obligatoire.';
      else if (field.number && value !== null && (!Number.isFinite(value) || (field.integer && !Number.isInteger(value)))) invalid[key] = field.integer ? 'Saisissez un nombre entier.' : 'Saisissez un nombre valide.';
      else if (field.min !== undefined && value < field.min) invalid[key] = `La valeur minimale est ${field.min}.`;
      else if (key === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) invalid[key] = 'Saisissez une adresse email valide.';
    }
    setErrors(invalid);
    if (Object.keys(invalid).length) { setError('Vérifiez les champs indiqués avant d’enregistrer.'); onInvalid?.(Object.keys(invalid)); return; }
    if (!Object.keys(payload).length) { cancel(); return; }
    pending.current = true; setSaving(true); setError('');
    try { await update(recordId, payload); setEditing(false); setChanges({}); onSaved(); }
    catch (e) { setError(e.response?.data?.message || e.message || 'Impossible d’enregistrer les modifications.'); }
    finally { pending.current = false; setSaving(false); }
  };
  return {
    editing: editing && allowed, changes, errors, error, saving, dirty, fields, excluded,
    value: key => Object.prototype.hasOwnProperty.call(changes, key) ? changes[key] : record?.[key],
    change: (key, value) => {
      setChanges(previous => {
        const next = { ...previous };
        const patch = deriveChanges ? deriveChanges(key, value, { ...record, ...previous }, excluded) : { [key]: value };
        for (const [name, changed] of Object.entries(patch)) {
          if (!fields[name] || excluded.includes(name)) continue;
          const initial = record?.[name] ?? null;
          if (changed === initial || (fields[name].boolean && changed === Boolean(initial))) delete next[name];
          else next[name] = changed;
        }
        return next;
      });
      setErrors(previous => ({ ...previous, [key]: undefined }));
    },
    start: () => { if (allowed) { setChanges({}); setErrors({}); setError(''); setEditing(true); } },
    cancel, leave, save,
  };
}

export function EditableValue({ name, label, children, multiline = false }) {
  const editor = useContext(EditContext);
  if (!editor?.editing || !editor.fields[name] || editor.excluded.includes(name)) return children;
  const field = editor.fields[name];
  const FieldComponent = field.component;
  const common = { 'aria-label': label, 'aria-invalid': Boolean(editor.errors[name]), disabled: editor.saving, status: editor.errors[name] ? 'error' : undefined };
  const value = editor.value(name);
  return <span className={`record-edit-field ${Object.prototype.hasOwnProperty.call(editor.changes, name) ? 'is-changed' : ''}`}>
    {FieldComponent ? <FieldComponent {...common} {...field.componentProps?.(editor)} value={value ?? undefined} onChange={next => editor.change(name, next ?? null)} style={{ width: '100%' }} />
      : field.boolean ? <Switch {...common} checked={Boolean(value)} onChange={next => editor.change(name, next)} checkedChildren="Oui" unCheckedChildren="Non" />
      : field.number ? <InputNumber {...common} value={value ?? null} onChange={next => editor.change(name, next)} controls={false} decimalSeparator="," />
        : multiline ? <Input.TextArea {...common} value={value ?? ''} autoSize={{ minRows: 2, maxRows: 6 }} onChange={event => editor.change(name, event.target.value || null)} />
          : <Input {...common} value={value ?? ''} onChange={event => editor.change(name, event.target.value || (field.required ? '' : null))} />}
    {editor.errors[name] && <small className="record-field-error">{editor.errors[name]}</small>}
  </span>;
}

export function RecordEditBar({ editor }) {
  if (!editor.editing) return null;
  return <div className="record-edit-bar">
    {editor.error && <Alert type="error" showIcon message={editor.error} />}
    <div className="record-edit-bar-row"><span><EditOutlined /> {editor.dirty ? 'Modifications non enregistrées' : 'Mode modification'}</span>
      <div className="record-actions"><Button aria-label="Annuler" icon={<CloseOutlined />} disabled={editor.saving} onClick={editor.cancel}>Annuler</Button><Button type="primary" aria-label="Sauvegarder" icon={<CheckOutlined />} loading={editor.saving} disabled={!editor.dirty} onClick={editor.save}>Sauvegarder</Button></div>
    </div>
  </div>;
}
export const EditingTag = () => <Tag icon={<EditOutlined />} color="blue">Modification</Tag>;
